import { create } from 'zustand';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';
import { ThemeKey } from '@common/constants/themes';

interface AppState {
  // User session
  msId: string | undefined;
  isLoggedIn: boolean;

  // UI errors
  hasMetasiteError: boolean;

  // Current flow state
  selectedCategory: string;
  storeName: string;
  selectedThemeColor: ThemeKey | null;
  userEmailAddress: string | undefined;
}

interface AppActions {
  // Session actions
  setMsId: (msId: string) => void;
  setIsLoggedIn: (value: boolean) => void;

  // Error actions
  setMetasiteError: (hasError: boolean) => void;
  resetMetasiteError: () => void;

  // Flow actions
  setSelectedCategory: (category: string) => void;
  resetSelectedCategory: () => void;
  setStoreName: (name: string) => void;
  setSelectedThemeColor: (color: ThemeKey | null) => void;
  setUserEmailAddress: (email: string) => void;
  // Reset
  resetStore: () => void;
}

type Store = AppState & AppActions;

const getInitialState = (): AppState => {
  const windowState = typeof window !== 'undefined' ? window.__INITIAL_STATE__ : undefined;

  return {
    msId: undefined,
    isLoggedIn: windowState?.isLoggedIn ?? false,
    hasMetasiteError: false,
    selectedCategory: '',
    storeName: '',
    selectedThemeColor: null,
    userEmailAddress: undefined,
  };
};

export const useAppStore = create<Store>()(
  devtools(
    persist(
      (set) => ({
        ...getInitialState(),
        setMsId: (msId) => set({ msId }),
        setIsLoggedIn: (isLoggedIn) => set({ isLoggedIn }),
        setMetasiteError: (hasMetasiteError) => set({ hasMetasiteError }),
        setSelectedCategory: (selectedCategory) => set({ selectedCategory }),
        setStoreName: (storeName) => set({ storeName }),
        setSelectedThemeColor: (selectedThemeColor) => set({ selectedThemeColor }),
        setUserEmailAddress: (userEmailAddress) => set({ userEmailAddress }),
        // Reset actions
        resetMetasiteError: () => set({ hasMetasiteError: false }),
        resetSelectedCategory: () => set({ selectedCategory: '' }),
        resetStore: () => set(getInitialState()),
      }),
      {
        name: 'ds-builder-session',
        storage: createJSONStorage(() => sessionStorage),
        partialize: (state) => ({
          msId: state.msId,
          selectedCategory: state.selectedCategory,
          selectedThemeColor: state.selectedThemeColor,
          userEmailAddress: state.userEmailAddress,
        }),
      },
    ),
    { name: 'AppStore' },
  ),
);

// Selectors
export const useIsLoggedIn = () => useAppStore((state) => state.isLoggedIn);
export const useMsId = () => useAppStore((state) => state.msId);
export const useHasMetasiteError = () => useAppStore((state) => state.hasMetasiteError);
export const useSelectedCategory = () => useAppStore((state) => state.selectedCategory);
export const useStoreName = () => useAppStore((state) => state.storeName);
export const useSelectedThemeColor = () => useAppStore((state) => state.selectedThemeColor);
export const useUserEmailAddress = () => useAppStore((state) => state.userEmailAddress);

// Action hooks
export const useSetMsId = () => useAppStore((state) => state.setMsId);
export const useSetIsLoggedIn = () => useAppStore((state) => state.setIsLoggedIn);
export const useSetMetasiteError = () => useAppStore((state) => state.setMetasiteError);
export const useSetSelectedCategory = () => useAppStore((state) => state.setSelectedCategory);
export const useSetStoreName = () => useAppStore((state) => state.setStoreName);
export const useSetSelectedThemeColor = () => useAppStore((state) => state.setSelectedThemeColor);
export const useResetMetasiteError = () => useAppStore((state) => state.resetMetasiteError);
export const useResetSelectedCategory = () => useAppStore((state) => state.resetSelectedCategory);
export const useResetStore = () => useAppStore((state) => state.resetStore);
export const useSetUserEmailAddress = () => useAppStore((state) => state.setUserEmailAddress);
